<template>
  <div
    v-if="!motionMinimal"
    class="field-atmosphere"
    :class="{ 'is-reduced': animationsCut }"
    aria-hidden="true"
  >
    <Transition name="field-xfade">
      <div
        v-if="ambience"
        :key="ambience.key + ambience.cardId"
        class="field-scene"
        :class="ambience.key"
      >
        <div
          class="field-art"
          :style="{ backgroundImage: `url(${artUrl})` }"
        />
        <div class="field-wash" />
        <div class="field-caustic" />
        <div class="field-ring" />
        <div class="field-fx">
          <span
            v-for="n in 16"
            :key="n"
            class="mote"
            :style="{ '--i': n }"
          />
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { getLocalCardImageUrl } from '../utils/cardImages'
import { useDuelPrefsStore } from '../stores/duelPrefs'
import type { FieldAmbience } from '../game/fieldAmbience'

const props = defineProps<{
  ambience: FieldAmbience | null
}>()

const motionMinimal = computed(
  () => useDuelPrefsStore().motionLevel === 'minimal',
)
const animationsCut = computed(
  () => useDuelPrefsStore().motionLevel !== 'full',
)

const artUrl = computed(() =>
  props.ambience ? getLocalCardImageUrl(props.ambience.cardId) : '',
)
</script>

<style scoped>
.field-atmosphere {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}

.field-scene {
  position: absolute;
  inset: 0;
}

.field-art {
  position: absolute;
  inset: -12%;
  background-size: cover;
  background-position: center 38%;
  filter: saturate(1.35) contrast(1.08) blur(18px);
  transform: scale(1.12);
  animation: field-drift 22s ease-in-out infinite alternate;
}

.field-wash {
  position: absolute;
  inset: 0;
}

.field-caustic {
  position: absolute;
  inset: -20%;
  opacity: 0.28;
  mix-blend-mode: screen;
}

.field-ring {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 18%;
  height: 18%;
  border: 2px solid rgba(255, 236, 170, 0.85);
  border-radius: 50%;
  transform: translate(-50%, -50%) scale(0.2);
  animation: field-ring 1.15s cubic-bezier(0.16, 1.15, 0.32, 1) both;
}

.field-fx {
  position: absolute;
  inset: 0;
}

.mote {
  position: absolute;
  left: calc(4% + (var(--i) * 5.8%));
  bottom: -8%;
  width: 7px;
  height: 11px;
  border-radius: 2px;
  opacity: 0;
  animation: mote-rise 7.5s calc(var(--i) * 0.28s) linear infinite;
}

.forest .field-wash {
  background:
    radial-gradient(ellipse at 50% 40%, rgba(34, 90, 28, 0.15), transparent 55%),
    linear-gradient(180deg, rgba(8, 28, 12, 0.55), rgba(4, 14, 8, 0.72));
}

.forest .mote {
  background: linear-gradient(180deg, #bbf7d0, #166534);
  border-radius: 0 70% 40% 70%;
}

.umi .field-wash {
  background:
    radial-gradient(ellipse at 50% 70%, rgba(14, 116, 166, 0.35), transparent 50%),
    linear-gradient(180deg, rgba(6, 24, 42, 0.5), rgba(3, 12, 28, 0.78));
}

.umi .field-caustic {
  background:
    repeating-radial-gradient(
      ellipse at 50% 110%,
      rgba(125, 230, 255, 0.22) 0 8px,
      transparent 12px 28px
    );
  animation: umi-caustic 9s linear infinite;
}

.umi .mote {
  width: 18px;
  height: 6px;
  border-radius: 50%;
  background: rgba(165, 243, 252, 0.7);
}

.yami .field-wash {
  background:
    radial-gradient(ellipse at 50% 45%, rgba(88, 28, 135, 0.42), transparent 52%),
    linear-gradient(180deg, rgba(12, 4, 22, 0.55), rgba(4, 2, 10, 0.82));
}

.yami .mote {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(192, 132, 252, 0.35);
  filter: blur(3px);
}

.sogen .field-wash {
  background:
    radial-gradient(ellipse at 50% 80%, rgba(202, 138, 4, 0.28), transparent 48%),
    linear-gradient(180deg, rgba(28, 32, 10, 0.45), rgba(12, 16, 6, 0.75));
}

.sogen .mote {
  width: 3px;
  height: 22px;
  background: linear-gradient(180deg, #fde68a, transparent);
}

.wasteland .field-wash {
  background:
    radial-gradient(ellipse at 50% 60%, rgba(180, 83, 9, 0.3), transparent 50%),
    linear-gradient(180deg, rgba(40, 22, 8, 0.5), rgba(18, 10, 4, 0.78));
}

.wasteland .mote {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #fbbf24;
}

.mountain .field-wash {
  background:
    radial-gradient(ellipse at 50% 20%, rgba(148, 163, 184, 0.28), transparent 46%),
    linear-gradient(180deg, rgba(18, 24, 38, 0.5), rgba(6, 10, 18, 0.78));
}

.mountain .mote {
  width: 28px;
  height: 10px;
  border-radius: 50%;
  background: rgba(226, 232, 240, 0.28);
  filter: blur(2px);
}

.generic .field-wash {
  background:
    radial-gradient(ellipse at 50% 40%, rgba(232, 197, 71, 0.2), transparent 50%),
    linear-gradient(180deg, rgba(12, 12, 18, 0.5), rgba(6, 8, 14, 0.78));
}

.field-xfade-enter-active,
.field-xfade-leave-active {
  transition: opacity 0.9s ease, transform 1.05s ease;
}

.field-xfade-enter-from {
  opacity: 0;
  transform: scale(1.08);
}

.field-xfade-leave-to {
  opacity: 0;
}

@keyframes field-drift {
  from {
    transform: scale(1.12) translate3d(-1.5%, 0, 0);
  }
  to {
    transform: scale(1.18) translate3d(1.5%, -1.5%, 0);
  }
}

@keyframes field-ring {
  0% {
    opacity: 0.95;
    transform: translate(-50%, -50%) scale(0.15);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(6.5);
  }
}

@keyframes mote-rise {
  0% {
    opacity: 0;
    transform: translate3d(0, 0, 0) rotate(0deg);
  }
  12% {
    opacity: 0.85;
  }
  100% {
    opacity: 0;
    transform: translate3d(18px, -110vh, 0) rotate(120deg);
  }
}

@keyframes umi-caustic {
  to {
    transform: translate3d(0, -8%, 0) rotate(8deg);
  }
}

.is-reduced .field-art,
.is-reduced .field-caustic,
.is-reduced .field-ring,
.is-reduced .mote {
  animation: none;
}

.is-reduced .field-xfade-enter-active,
.is-reduced .field-xfade-leave-active {
  transition: opacity 0.35s ease;
}

.is-reduced .field-xfade-enter-from {
  transform: none;
}

@media (prefers-reduced-motion: reduce) {
  .field-art,
  .field-caustic,
  .field-ring,
  .mote {
    animation: none;
  }

  .field-xfade-enter-active,
  .field-xfade-leave-active {
    transition: opacity 0.35s ease;
  }

  .field-xfade-enter-from {
    transform: none;
  }
}
</style>
